import {
  Box,
  Flex,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useMixpanel,
  usePortal,
  Z_INDEX,
} from '@talos/kyoko';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useOrderFormToggle } from '../../../hooks/useOrderFormToggle';
import { useMobileModal } from '../../../providers/MobileModalProvider';
import type { NavigationItem } from '../../../tokens/navigationPaths';
import { OverflowModal } from './OverflowModal';
import { TAB_BAR_ACCESSORIES_PORTAL_ID } from './TabBarAccessories';
import { TabButton } from './TabButton';
import { SelectedIndicatorWrapper, TAB_BAR_HEIGHT, TabBarAccessoriesWrapper, TabBarWrapper } from './styles';

const MOBILE_TAB_BAR_HORIZONTAL_SPACING = 4;

export function TabBar({
  items,
  currentRoute,
  layoutSectionLabel,
}: {
  items: NavigationItem[];
  currentRoute: NavigationItem;
  layoutSectionLabel?: string;
}) {
  const [savedItemsOrder, setSavedItemsOrder] = useLocalStorage<string[]>(TAB_BAR_ORDER_STORAGE_KEY, []);
  const orderedItems = useMemo(() => sortItemsBySavedOrder(items, savedItemsOrder ?? []), [items, savedItemsOrder]);
  const hasOverflowItem = orderedItems.length > MAX_ITEMS_WITHOUT_OVERFLOW;
  const visibleItems = hasOverflowItem ? orderedItems.slice(0, VISIBLE_ITEMS_WITH_OVERFLOW) : orderedItems;
  const selectedVisibleIndex = visibleItems.findIndex(item => item.path === currentRoute.path);
  const selectedIndex = hasOverflowItem && selectedVisibleIndex === -1 ? visibleItems.length : selectedVisibleIndex;
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [tabButtonWidth, setTabButtonWidth] = useState(0);
  const tabButtonCount = Math.max(visibleItems.length + (hasOverflowItem ? 1 : 0), 1);

  useLayoutEffect(() => {
    setTabButtonWidth(((tabBarRef.current?.offsetWidth ?? 0) - MOBILE_TAB_BAR_HORIZONTAL_SPACING * 2) / tabButtonCount);
  }, [tabBarRef.current?.offsetWidth, tabButtonCount]);

  const { setPortalRef } = usePortal(TAB_BAR_ACCESSORIES_PORTAL_ID);

  const mixpanel = useMixpanel(MixpanelEventSource.MobileTabBar);

  const { openModal, closeModal, isModalOpen } = useMobileModal();

  const toggleOrderForm = useOrderFormToggle();
  const handleOpenOMS = useCallback(() => {
    toggleOrderForm();
  }, [toggleOrderForm]);

  const handleItemsChange = useCallback(
    (nextItems: NavigationItem[]) => {
      setSavedItemsOrder(nextItems.map(getNavItemKey));
    },
    [setSavedItemsOrder]
  );

  const trackNavigateToItem = useCallback(
    (item: NavigationItem) => {
      if (item.path == null) {
        return;
      }
      mixpanel.track(MixpanelEvent.NavigateTo, { [MixpanelEventProperty.Path]: item.path });
      if (item.mixpanelEvent) {
        mixpanel.track(item.mixpanelEvent);
      }
    },
    [mixpanel]
  );

  const handleOpenOverflow = useCallback(() => {
    if (isModalOpen) {
      closeModal();
      return;
    }
    openModal({
      content: <OverflowModal items={orderedItems} onItemsChange={handleItemsChange} title={layoutSectionLabel} />,
    });
    mixpanel.track(MixpanelEvent.OpenMobileTabBarOverflow);
  }, [openModal, closeModal, isModalOpen, orderedItems, handleItemsChange, layoutSectionLabel, mixpanel]);

  return (
    <Box position="fixed" bottom="21px" left="21px" right="21px" zIndex={Z_INDEX.tabBar}>
      <TabBarAccessoriesWrapper ref={setPortalRef} />
      <Flex gap="spacing8">
        <TabBarWrapper flex="1" ref={tabBarRef} data-testid="mobile-tab-bar">
          <SelectedIndicatorWrapper
            w={tabButtonWidth}
            transform={`translateX(${tabButtonWidth * Math.max(selectedIndex, 0)}px)`}
          />
          {visibleItems.map((item, index) => (
            <TabButton
              key={getNavItemKey(item)}
              item={item}
              isSelected={index === selectedIndex}
              onPress={() => trackNavigateToItem(item)}
            />
          ))}
          {hasOverflowItem && (
            <TabButton
              item={OVERFLOW_ITEM}
              isSelected={selectedIndex === visibleItems.length}
              onPress={handleOpenOverflow}
            />
          )}
        </TabBarWrapper>
        <TabBarWrapper w={TAB_BAR_HEIGHT} onClick={handleOpenOMS}>
          <TabButton item={TRADE_ITEM} isSelected={false} />
        </TabBarWrapper>
      </Flex>
    </Box>
  );
}

const TRADE_ITEM: NavigationItem = {
  id: 'trade',
  label: 'Trade',
  icon: IconName.TrendUp,
};

const OVERFLOW_ITEM: NavigationItem = {
  id: 'more',
  label: 'More',
  icon: IconName.DotsHorizontal,
};

const MAX_ITEMS_WITHOUT_OVERFLOW = 4;
const VISIBLE_ITEMS_WITH_OVERFLOW = 3;
const TAB_BAR_ORDER_STORAGE_KEY = 'mobile-tab-bar-order';

function sortItemsBySavedOrder(items: NavigationItem[], savedItemsOrder: string[]) {
  if (savedItemsOrder.length === 0) {
    return items;
  }

  const positions = new Map(savedItemsOrder.map((id, index) => [id, index]));
  return items.toSorted((a, b) => {
    const positionA = positions.get(getNavItemKey(a)) ?? Number.MAX_SAFE_INTEGER;
    const positionB = positions.get(getNavItemKey(b)) ?? Number.MAX_SAFE_INTEGER;
    return positionA - positionB;
  });
}

function getNavItemKey(item: NavigationItem) {
  return item.id ?? item.path ?? '';
}
